import { Criteria, CriteriaPurpose, Policy, Purpose } from '../../types';

export type SearchScope = 'names' | 'policies' | 'both';

export const DEFAULT_SEARCH_SCOPE: SearchScope = 'both';

/** Idle table preview length. Long enough to skim, short enough that rows stay one-line. */
export const IDLE_POLICY_PREVIEW_CHARS = 160;

/** How many current-Policy hit windows to show on one row while searching. */
export const MAX_POLICY_SNIPPETS = 3;

/** Characters of context on each side of a hit inside a snippet. */
export const POLICY_SNIPPET_RADIUS = 40;

export const currentPolicy = (criterion: Criteria, policies: Policy[]): Policy | undefined => {
  if (!criterion.policyId) return undefined;
  return policies.find(policy => policy.id === criterion.policyId);
};

/**
 * A Criterion's Policy Versions, newest first.
 *
 * Version number is the authority (ADR-0001). Null versions sort last; creation
 * date breaks ties and is the fallback when version is missing.
 */
export const policiesForCriterion = (policies: Policy[], criteriaId: string): Policy[] =>
  policies
    .filter(policy => policy.criteriaId === criteriaId)
    .sort((left, right) => {
      const leftVersion = left.version ?? Number.NEGATIVE_INFINITY;
      const rightVersion = right.version ?? Number.NEGATIVE_INFINITY;
      if (rightVersion !== leftVersion) return rightVersion - leftVersion;
      return new Date(right.creationDate).getTime() - new Date(left.creationDate).getTime();
    });

export const policyVersionOptionLabel = (policy: Policy, isCurrent: boolean): string => {
  const version = policy.version != null ? `v${policy.version}` : 'unversioned';
  const date = new Date(policy.creationDate).toLocaleDateString('en-GB');
  return isCurrent ? `${version} · ${date} (current)` : `${version} · ${date}`;
};

export const purposesForCriterion = (
  criterionId: string,
  criteriaPurposes: CriteriaPurpose[],
  allPurposes: Purpose[]
): Purpose[] =>
  criteriaPurposes
    .filter(cp => cp.criteriaId === criterionId)
    .map(cp => allPurposes.find(purpose => purpose.id === cp.purposeId))
    .filter((purpose): purpose is Purpose => purpose !== undefined);

export const countHits = (text: string, query: string): number => {
  const needle = query.trim().toLowerCase();
  if (!needle || !text) return 0;
  const haystack = text.toLowerCase();
  let count = 0;
  let found = haystack.indexOf(needle);
  while (found !== -1) {
    count += 1;
    found = haystack.indexOf(needle, found + Math.max(needle.length, 1));
  }
  return count;
};

/** Whole lines of the current Policy that contain the query, untruncated. */
export const matchingPolicyLines = (text: string, query: string): string[] => {
  const needle = query.trim();
  if (!needle || !text) return [];
  return text.split('\n').filter(line => line.toLowerCase().includes(needle.toLowerCase()));
};

export const rankedByHits = (
  criteria: Criteria[],
  policies: Policy[],
  query: string,
  scope: SearchScope = DEFAULT_SEARCH_SCOPE
): Criteria[] => {
  const needle = query.trim();
  if (!needle || scope === 'names') {
    return [...criteria].sort((left, right) => left.name.localeCompare(right.name));
  }
  return [...criteria].sort((left, right) => {
    const leftHits = countHits(currentPolicy(left, policies)?.text ?? '', needle);
    const rightHits = countHits(currentPolicy(right, policies)?.text ?? '', needle);
    if (rightHits !== leftHits) return rightHits - leftHits;
    return left.name.localeCompare(right.name);
  });
};

export const matchesCriterionSearch = (
  criterion: Criteria,
  currentPolicyText: string | undefined,
  query: string,
  scope: SearchScope = DEFAULT_SEARCH_SCOPE
): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const nameHit = scope !== 'policies' && criterion.name.toLowerCase().includes(needle);
  const policyHit = scope !== 'names' && (currentPolicyText ?? '').toLowerCase().includes(needle);
  return nameHit || policyHit;
};

export const truncatePolicyPreview = (text: string): string => {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  if (collapsed.length <= IDLE_POLICY_PREVIEW_CHARS) return collapsed;
  return `${collapsed.slice(0, IDLE_POLICY_PREVIEW_CHARS).trimEnd()}…`;
};

const mergeWindows = (
  windows: { start: number; end: number }[]
): { start: number; end: number }[] => {
  if (windows.length === 0) return [];
  const sorted = [...windows].sort((left, right) => left.start - right.start);
  const first = sorted[0];
  if (!first) return [];
  const merged: { start: number; end: number }[] = [{ ...first }];
  for (const window of sorted.slice(1)) {
    const last = merged[merged.length - 1];
    if (last && window.start <= last.end) {
      last.end = Math.max(last.end, window.end);
    } else {
      merged.push({ ...window });
    }
  }
  return merged;
};

/**
 * Short windows around each current-Policy hit, capped per Criterion.
 *
 * Overlapping windows are merged so a dense cluster of hits does not flood the row.
 */
export const policySnippets = (text: string, query: string): string[] => {
  const needle = query.trim();
  if (!needle || !text) return [];

  const haystack = text.toLowerCase();
  const normalised = needle.toLowerCase();
  const windows: { start: number; end: number }[] = [];
  let found = haystack.indexOf(normalised);

  while (found !== -1) {
    windows.push({
      start: Math.max(0, found - POLICY_SNIPPET_RADIUS),
      end: Math.min(text.length, found + normalised.length + POLICY_SNIPPET_RADIUS),
    });
    found = haystack.indexOf(normalised, found + Math.max(normalised.length, 1));
  }

  return mergeWindows(windows)
    .slice(0, MAX_POLICY_SNIPPETS)
    .map(window => {
      const prefix = window.start > 0 ? '…' : '';
      const suffix = window.end < text.length ? '…' : '';
      const body = text.slice(window.start, window.end).replace(/\s+/g, ' ').trim();
      return `${prefix}${body}${suffix}`;
    });
};
